import type {
  CreateServiceStandardFormDTO,
  UpdateServiceStandardFormDTO,
} from "@/modules/service-standard/domain/contracts";
import type {
  ServiceStandardByIdResult,
  ServiceStandardsListResult,
} from "@/modules/service-standard/domain/types";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { toast } from "@devhop/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { serviceStandardApi, serviceChannelApi, serviceStandardMappingApi, type ServiceChannelDTO, type ServiceChannelsListResult, type ServiceStandardMappingDTO, type ServiceStandardMappingListResult } from "./client";

export const serviceStandardKeys = {
  all: ["service-standard"] as const,
  list: (q: Partial<OffsetPageQueryDTO>) => ["service-standard", "list", q] as const,
  detail: (id: string) => ["service-standard", "detail", id] as const,
};

export function useServiceStandardsQuery(query: Partial<OffsetPageQueryDTO> = {}) {
  const q: OffsetPageQueryDTO = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
  };
  return useQuery<ServiceStandardsListResult>({
    queryKey: serviceStandardKeys.list(q),
    queryFn: () => serviceStandardApi.list(q),
  });
}

export function useServiceStandardQuery(id: string) {
  return useQuery<ServiceStandardByIdResult>({
    queryKey: serviceStandardKeys.detail(id),
    queryFn: () => serviceStandardApi.get(id),
    enabled: !!id,
  });
}

export function useCreateServiceStandard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateServiceStandardFormDTO) => serviceStandardApi.create(input),
    onSuccess: () => {
      toast.success("ສ້າງມາດຕະຖານບໍລິການສໍາເລັດ");
      qc.invalidateQueries({ queryKey: serviceStandardKeys.all });
    },
    onError: () => toast.error("ສ້າງມາດຕະຖານບໍລິການລົ້ມເຫຼວ"),
  });
}

export function useDuplicateServiceStandard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => serviceStandardApi.duplicate(id),
    onSuccess: () => {
      toast.success("ຄັດລອກມາດຕະຖານບໍລິການສໍາເລັດ");
      qc.invalidateQueries({ queryKey: serviceStandardKeys.all });
    },
    onError: () => toast.error("ຄັດລອກມາດຕະຖານບໍລິການລົ້ມເຫຼວ"),
  });
}

export function useUpdateServiceStandard(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateServiceStandardFormDTO) => serviceStandardApi.update(id, input),
    onSuccess: () => {
      toast.success("ແກ້ໄຂມາດຕະຖານບໍລິການສໍາເລັດ");
      qc.invalidateQueries({ queryKey: serviceStandardKeys.detail(id) });
      qc.invalidateQueries({ queryKey: serviceStandardKeys.all });
    },
    onError: () => toast.error("ແກ້ໄຂມາດຕະຖານບໍລິການລົ້ມເຫຼວ"),
  });
}

export function useDeleteServiceStandard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => serviceStandardApi.delete(id),
    onSuccess: () => {
      toast.success("ລຶບມາດຕະຖານບໍລິການສໍາເລັດ");
      qc.invalidateQueries({ queryKey: serviceStandardKeys.all });
    },
    onError: () => toast.error("ລຶບມາດຕະຖານບໍລິການລົ້ມເຫຼວ"),
  });
}

// Service Channel Queries
export const serviceChannelKeys = {
  all: ["service-channel"] as const,
  list: (q: Partial<OffsetPageQueryDTO>) => ["service-channel", "list", q] as const,
  detail: (id: string) => ["service-channel", "detail", id] as const,
};

export function useServiceChannels(query: Partial<OffsetPageQueryDTO> = {}) {
  const q: OffsetPageQueryDTO = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
  };
  return useQuery<ServiceChannelsListResult>({
    queryKey: serviceChannelKeys.list(q),
    queryFn: () => serviceChannelApi.list(q),
  });
}

export function useServiceChannel(id: string) {
  return useQuery<ServiceChannelDTO>({
    queryKey: serviceChannelKeys.detail(id),
    queryFn: () => serviceChannelApi.get(id),
    enabled: !!id,
  });
}

export function useCreateServiceChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; description?: string | null; orderIndex?: number }) => serviceChannelApi.create(input),
    onSuccess: () => {
      toast.success("ສ້າງຊ່ອງທາງບໍລິການສໍາເລັດ");
      qc.invalidateQueries({ queryKey: serviceChannelKeys.all });
    },
    onError: () => toast.error("ສ້າງຊ່ອງທາງບໍລິການລົ້ມເຫຼວ"),
  });
}

export function useUpdateServiceChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: { name?: string; description?: string | null; orderIndex?: number } }) => serviceChannelApi.update(id, input),
    onSuccess: (_, variables) => {
      toast.success("ແກ້ໄຂຊ່ອງທາງບໍລິການສໍາເລັດ");
      qc.invalidateQueries({ queryKey: serviceChannelKeys.detail(variables.id) });
      qc.invalidateQueries({ queryKey: serviceChannelKeys.all });
    },
    onError: () => toast.error("ແກ້ໄຂຊ່ອງທາງບໍລິການລົ້ມເຫຼວ"),
  });
}

export function useDeleteServiceChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => serviceChannelApi.delete(id),
    onSuccess: () => {
      toast.success("ລຶບຊ່ອງທາງບໍລິການສໍາເລັດ");
      qc.invalidateQueries({ queryKey: serviceChannelKeys.all });
    },
    onError: () => toast.error("ລຶບຊ່ອງທາງບໍລິການລົ້ມເຫຼວ"),
  });
}

// Service Standard Mapping Queries
export const serviceStandardMappingKeys = {
  all: ["service-standard-mapping"] as const,
  list: (serviceStandardId: string) => ["service-standard-mapping", "list", serviceStandardId] as const,
  listAll: (query: Partial<{
    offset: number;
    limit: number;
    sort?: Array<{ field: string; dir: "asc" | "desc" }>;
    filters?: Array<{ field: string; op: string; value: unknown }>;
    serviceStandardId?: string;
    positionId?: string;
    checklistId?: string;
    qcStandardId?: string;
    kpiId?: string;
    search?: string;
  }>) => ["service-standard-mapping", "list-all", query] as const,
};

export function useServiceStandardMappings(serviceStandardId: string) {
  return useQuery<{ data: ServiceStandardMappingDTO[] }>({
    queryKey: serviceStandardMappingKeys.list(serviceStandardId),
    queryFn: () => serviceStandardMappingApi.getMappings(serviceStandardId),
    enabled: !!serviceStandardId,
  });
}

export function useCreateServiceStandardMapping(serviceStandardId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      positionId?: string | null;
      checklistId?: string | null;
      qcStandardId?: string | null;
      kpiId?: string | null;
    }) => serviceStandardMappingApi.createMapping(serviceStandardId, data),
    onSuccess: () => {
      toast.success("ສ້າງການເຊື່ອມຕໍ່ສໍາເລັດ");
      qc.invalidateQueries({ queryKey: serviceStandardMappingKeys.list(serviceStandardId) });
      qc.invalidateQueries({ queryKey: serviceStandardKeys.detail(serviceStandardId) });
    },
    onError: () => toast.error("ສ້າງການເຊື່ອມຕໍ່ລົ້ມເຫຼວ"),
  });
}

export function useUpdateServiceStandardMapping(serviceStandardId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: {
      positionId?: string | null;
      checklistId?: string | null;
      qcStandardId?: string | null;
      kpiId?: string | null;
    }}) => serviceStandardMappingApi.updateMapping(id, data),
    onSuccess: () => {
      toast.success("ແກ້ໄຂການເຊື່ອມຕໍ່ສໍາເລັດ");
      qc.invalidateQueries({ queryKey: serviceStandardMappingKeys.list(serviceStandardId) });
      qc.invalidateQueries({ queryKey: serviceStandardKeys.detail(serviceStandardId) });
    },
    onError: () => toast.error("ແກ້ໄຂການເຊື່ອມຕໍ່ລົ້ມເຫຼວ"),
  });
}

export function useDeleteServiceStandardMapping(serviceStandardId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => serviceStandardMappingApi.deleteMapping(id),
    onSuccess: () => {
      toast.success("ລຶບການເຊື່ອມຕໍ່ສໍາເລັດ");
      qc.invalidateQueries({ queryKey: serviceStandardMappingKeys.list(serviceStandardId) });
      qc.invalidateQueries({ queryKey: serviceStandardKeys.detail(serviceStandardId) });
      qc.invalidateQueries({ queryKey: serviceStandardMappingKeys.all });
    },
    onError: () => toast.error("ລຶບການເຊື່ອມຕໍ່ລົ້ມເຫຼວ"),
  });
}

export function useServiceStandardMappingsList(query: Partial<{
  offset: number;
  limit: number;
  sort?: Array<{ field: string; dir: "asc" | "desc" }>;
  filters?: Array<{ field: string; op: string; value: unknown }>;
  serviceStandardId?: string;
  positionId?: string;
  checklistId?: string;
  qcStandardId?: string;
  kpiId?: string;
  search?: string;
}> = {}) {
  return useQuery<ServiceStandardMappingListResult>({
    queryKey: serviceStandardMappingKeys.listAll(query),
    queryFn: () => serviceStandardMappingApi.listMappings(query),
  });
}

